import { useSelector } from 'react-redux';
import { sessionSelector } from 'app/store/session/selectors';
import {
  MatchAccessibility,
  MatchPlayerRole,
  MatchPlayerStatus,
  MatchStatus,
} from '@okletsplay/core';
import { useMemo } from 'react';
import { MatchEntity } from 'app/interfaces/entity';

export const useMatchStatus = (match: MatchEntity) => {
  const user = useSelector(sessionSelector.activeUser);

  const currentUserPlayer = useMemo(
    () => match?.players?.find((player) => player.userId === user.userId),
    [match, user]
  );
  const opponentPlayer = useMemo(
    () => match?.players?.find((player) => player.userId !== user.userId),
    [match, user]
  );
  const ownerPlayer = useMemo(
    () => match?.players?.find((player) => player.role === MatchPlayerRole.Owner),
    [match]
  );
  const currentUserResult = useMemo(
    () => match?.results?.find((result) => result.userId === user.userId),
    [match, user]
  );
  const opponentResult = useMemo(
    () => match?.results?.find((result) => result.userId !== user.userId),
    [match, user]
  );

  const isFinishedMatch = useMemo(() => match?.status === MatchStatus.Finished, [match?.status]);

  const isPublic = match.accessibility === MatchAccessibility.Public;
  const isPrivate = match.accessibility === MatchAccessibility.Private;
  const isCurrentUserOwner = currentUserPlayer?.role === MatchPlayerRole.Owner;
  const isCurrentUserPlayer = currentUserPlayer?.role === MatchPlayerRole.Player;
  const joinRequestsCount = useMemo(
    () =>
      match?.players?.filter(
        (player) =>
          player.status === MatchPlayerStatus.Requested &&
          player.userId !== currentUserPlayer?.userId
      )?.length,
    [currentUserPlayer?.userId, match.players]
  );

  return {
    isPublic,
    isPrivate,
    isCurrentUserOwner,
    isCurrentUserPlayer,
    isFinishedMatch,
    currentUserPlayer,
    opponentPlayer,
    ownerPlayer,
    currentUserResult,
    opponentResult,
    joinRequestsCount,
  };
};
